/**
 * Ряд «Сослаться на фото» под промптом: чипы-фото, клик вставляет в текст
 * пилюлю-ссылку на этот вход; наведение показывает поповер — крупное фото и
 * подсказку, что делает клик (владелец 15.09: «при наведении на пилюлю с фото
 * показать увеличение и инструкцию, как в виджете»).
 *
 * ОДИН компонент на все места с таким рядом: виджет генерации, узел-промпт
 * мастерской, сценарий карточки товара — раньше поповер был только у виджета.
 * Поповер — fixed через portal: края виджета его не режут. На холсте
 * мастерской — `inline`: попап живёт в ряду над своим чипом (как панель
 * NodePopover), двигается и масштабируется с холстом; портал во вьюпорте там
 * не привязан к чипу и при зуме выглядит великаном рядом с узлом (владелец 15.09).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'

import { Icon } from '../../public/icons/Icon.tsx'

export interface PillInsertChip {
	id: string
	/** Подпись чипа (без «＋»). */
	label: string
	/** Класс-род чипа: p1…p4 | frame1 | frame2 | char | video | audio. */
	cls: string
	url: string | null
	/** Заголовок поповера (обычно = label). */
	popTitle: string
	/** Подсказка, когда файла нет (чип-приглашение слота карточки). */
	emptyHint?: string
	/** Файл приложен, но промпт его не упоминает — модель решит сама, что с ним делать
	 *  (utils/pillMentions): янтарная точка на чипе и подсказка вставить ссылку. */
	unmentioned?: boolean
	onInsert: () => void
}

const HIDE_DELAY = 160
const POP_W = 212

function hintFor(cls: string): string {
	if (cls === 'video') return 'Клик вставит в промпт пилюлю-ссылку на это видео — туда, где стоит курсор. Пишите сцену и указывайте пилюлей, где именно использовать это видео.'
	if (cls === 'audio') return 'Клик вставит в промпт пилюлю-ссылку на этот звук — туда, где стоит курсор.'
	return 'Клик вставит в промпт пилюлю-ссылку на это фото — туда, где стоит курсор. Пишите сцену и указывайте пилюлей, где именно использовать это фото.'
}

export default function PillInsertChips({ chips, className, title = 'Сослаться на фото:', inline = false }: {
	chips: PillInsertChip[]
	className?: string
	title?: string
	/** Попап над своим чипом внутри ряда (absolute), а не fixed-портал — для холста. */
	inline?: boolean
}) {
	const [pop, setPop] = useState<{ id: string; left: number; top: number; below: boolean } | null>(null)
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
	useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

	const show = (id: string, el: HTMLElement) => {
		if (timer.current) clearTimeout(timer.current)
		if (inline) {
			setPop({ id, left: 0, top: 0, below: false })
			return
		}
		const cr = el.getBoundingClientRect()
		// Наверху экрана поповеру некуда раскрыться — показываем под чипом.
		const below = cr.top < 230
		setPop({ id, left: Math.max(8, Math.min(cr.left, window.innerWidth - POP_W)), top: below ? cr.bottom + 6 : cr.top - 6, below })
	}
	const hideSoon = () => {
		if (timer.current) clearTimeout(timer.current)
		timer.current = setTimeout(() => setPop(null), HIDE_DELAY)
	}
	const keep = () => { if (timer.current) clearTimeout(timer.current) }

	if (chips.length === 0) return null
	const cur = pop ? chips.find((c) => c.id === pop.id) : undefined

	const renderPop = (c: PillInsertChip, mode: 'fixed' | 'inline', style?: CSSProperties) => (
		<div
			className={`gm__ppill-pop gm__ppill-pop--${mode}`}
			role="tooltip"
			style={style}
			onMouseEnter={keep}
			onMouseLeave={hideSoon}
		>
			{c.url ? (
				c.cls === 'video' ? (
					<video src={c.url} className="gm__ppill-pop__img" muted playsInline preload="metadata" />
				) : c.cls === 'audio' ? (
					<div className="gm__ppill-pop__img gm__ppill-pop__img--audio"><Icon id="gen-audio" size={22} /></div>
				) : (
					<img src={c.url} alt="" className="gm__ppill-pop__img" />
				)
			) : (
				<div className="gm__ppill-pop__img gm__ppill-pop__img--empty">фото не загружено</div>
			)}
			<div className="gm__ppill-pop__title">{c.popTitle}</div>
			{c.url && c.unmentioned && (
				<div className="gm__ppill-pop__sub gm__ppill-pop__sub--warn">Файл не упомянут в промпте — модель сама решит, что с ним делать. Клик вставит ссылку.</div>
			)}
			{c.url ? (
				<div className="gm__ppill-pop__sub">{hintFor(c.cls)}</div>
			) : (
				<div className="gm__ppill-pop__sub gm__ppill-pop__sub--warn">{c.emptyHint || 'Сначала загрузите фото в этот слот.'}</div>
			)}
		</div>
	)

	return (
		<div className={`gm__pill-insert${className ? ` ${className}` : ''}`}>
			<span className="gm__pill-insert__t">{title}</span>
			{chips.map((c) => (
				<span key={c.id} className="gm__pill-insert__item">
					<button
						type="button"
						className={`gm__pill-insert__chip gm__pill-insert__chip--${c.cls}${c.url ? '' : ' gm__pill-insert__chip--empty'}${c.url && c.unmentioned ? ' gm__pill-insert__chip--unmentioned' : ''}`}
						title={c.url && c.unmentioned ? 'Не упомянут в промпте — модель решит сама. Клик вставит ссылку.' : undefined}
						onMouseEnter={(e) => show(c.id, e.currentTarget)}
						onMouseLeave={hideSoon}
						onFocus={(e) => show(c.id, e.currentTarget)}
						onBlur={hideSoon}
						// Не отбирать фокус у поля: пилюля ложится туда, где каретка.
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => {
							c.onInsert()
							setPop(null)
						}}
					>
						{c.url && c.cls !== 'video' && c.cls !== 'audio' && (
							<img src={c.url} alt="" className="gm__pill-insert__img" draggable={false} />
						)}
						＋ {c.label}
					</button>
					{inline && pop?.id === c.id && renderPop(c, 'inline')}
				</span>
			))}
			{chips.some((c) => c.url && c.unmentioned) && (
				<span className="gm__pill-insert__note">не упомянуты в промпте — модель решит сама, что с ними делать</span>
			)}
			{!inline && pop && cur && createPortal(
				renderPop(cur, 'fixed', { left: pop.left, top: pop.below ? pop.top : undefined, bottom: pop.below ? undefined : window.innerHeight - pop.top }),
				document.body,
			)}
		</div>
	)
}
